import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Header } from "@/components/layout/Header";
import { MatchPulse } from "@/components/engagement/MatchPulse";
import { CommunicationTabs } from "@/components/communication/CommunicationTabs";
import { QuinielaOpenCTA } from "@/components/predictions/QuinielaOpenCTA";
import { QuinielaTopCTA } from "@/components/predictions/QuinielaTopCTA";
import { DiscoveryDailyCTA } from "@/components/retention/DiscoveryDailyCTA";
import { GuerrillaInviteButton } from "@/components/viral/GuerrillaInviteButton";
import { LivePollWidget } from "@/components/polls/LivePollWidget";
import { PredictionCard } from "@/components/predictions/PredictionCard";
import { QuickStats } from "@/components/stats/QuickStats";
import { BottomNav, type MobileTab } from "@/components/mobile/BottomNav";
import { MobileShareInlineCard } from "@/components/mobile/MobileShareDock";

import { firePremiumGoal } from "@/components/v4/PremiumGoalCelebration";
import { NationOvertakeToast } from "@/components/nationwars/NationOvertakeToast";
import { getProFrame, setActiveRoster } from "@/lib/simulation/professionalSequence";
import { getMatchRoster, rosterSummary } from "@/lib/sports/matchRosters";
import { trackEvent } from "@/lib/audit";
import { PREVIEW_MODE } from "@/lib/previewMode";
import { bootstrapSession } from "@/lib/session";
import { captureIncomingReferral } from "@/lib/referrals";
import { InstallBanner } from "@/components/pwa/InstallBanner";
import { useDemoTrigger } from "@/lib/demo-control";
import { useIpProtection, isIpProtectionRequested } from "@/lib/ip-protection";
import type { ChatMessage } from "@/types";
import { Sparkles } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { useFlag } from "@/lib/stability/featureFlags";
import { TacticalMiniMap } from "@/components/radar/TacticalMiniMap";
import { PredictiveVector } from "@/components/radar/PredictiveVector";
import { CompactTacticalXrayCard } from "@/components/match/CompactTacticalXrayCard";
import { PitchTeamIdentityBar } from "@/components/radar/PitchTeamIdentityBar";
import { useOnboarding } from "@/hooks/useOnboarding";
import { NationGate } from "@/components/onboarding/NationGate";
import { NationPositionBadge } from "@/components/nationwars/NationPositionBadge";
import { NationMomentumLine } from "@/components/nationwars/NationMomentumLine";
import { ClipOfTheDayCard } from "@/components/home/ClipOfTheDayCard";
import { AudioSyncEduCard } from "@/components/home/AudioSyncEduCard";
import { Day2ReturnPanel } from "@/components/home/Day2ReturnPanel";
import { IncomingChallengeBanner } from "@/components/challenges/IncomingChallengeBanner";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAdEngine } from "@/hooks/useAdEngine";
import { RadarSmartAd } from "@/components/ads/RadarSmartAd";
import { useMatchHub } from "@/hooks/useMatchHub";
import { useMatchInsights, useRealMatchDetailsEnabled } from "@/hooks/useMatchInsights";
import { useProviderFixtureIds } from "@/hooks/useProviderFixtureIds";
import { useRealFixtureMatches } from "@/hooks/useRealFixtureMatches";
import { GlobalScoreBar } from "@/components/layout/GlobalScoreBar";
import { NoLiveMatch } from "@/components/match/NoLiveMatch";
import { MatchCalendar } from "@/components/match/MatchCalendar";
import { DemoModeBanner } from "@/components/match/DemoModeBanner";
import { useDemoModeAllowed } from "@/hooks/useDemoModeAllowed";
import { usePublicDemoPreview } from "@/hooks/usePublicDemoPreview";
import { CrossMatchToast } from "@/components/matches/CrossMatchToast";
import { useMultiMatch } from "@/hooks/useMultiMatch";
import { useFanPoints } from "@/hooks/useFanPoints";
import { EdgeAlerts } from "@/components/radar/EdgeAlerts";
import { VarWidget } from "@/components/widgets/VarWidget";
import { EventTimeline } from "@/components/widgets/EventTimeline";
import { OfficialLineups } from "@/components/lineups/OfficialLineups";
import { OfficialPlayerStats } from "@/components/players/OfficialPlayerStats";
import { useMode } from "@/lib/mode";
import {
  evaluateSmartBonus,
  isResponsibleGamingAllowed,
  type SmartBonusOffer,
} from "@/lib/betting/smartBonusEngine";
import { SmartBonusPopup } from "@/components/betting/SmartBonusPopup";
import { GeoDynamicBanner } from "@/components/ads/GeoDynamicBanner";
import { ActivationBanner } from "@/components/onboarding/ActivationBanner";
import { SponsorRivalryPoll } from "@/components/home/SponsorRivalryPoll";
import { NextMissionCard } from "@/components/conversion/NextMissionCard";
import { StreakRecoveryCard } from "@/components/retention/StreakRecoveryCard";
import { ResultMomentCard } from "@/components/retention/ResultMomentCard";
import type { HubTrigger } from "@/components/engagement/HalftimeEngagementHub";
import { allowAutoOpen } from "@/lib/halftimeAutoOpenPolicy";
import { evaluateAutoOpen } from "@/lib/pr36h2b/entertainmentHubMode";
import { AuditTransparencyTab } from "@/components/profile/AuditTransparencyTab";
import { LiveCommentaryEngine } from "@/lib/ai/liveCommentaryEngine";
import { notify } from "@/components/ui/NotificationDock";
import { getActiveMatchContext, buildRichMatchContext, formatRichContextForAi } from "@/lib/activeMatch";
import { onOpenAdvancedStats } from "@/lib/advancedStats";
import { deriveMatchClock, buildAiTemporalContext } from "@/lib/matchClock";
import { activateGoalCleanMode, useCleanMode, useKeyboardOpen } from "@/lib/uiOverlays";
import { useActiveCtaContext, ctaLabelKey, useRegisterCtaContext } from "@/lib/pr29a/ctaContext";
import { setLayerActive } from "@/lib/pr30/zeroOverlapPolicy";
import { useDraggableFab } from "@/lib/pr27/draggableFab";
import { activateDangerPlay, evaluateThreatWindow } from "@/lib/dangerPlayMode";
import { DangerDebugOverlay } from "@/components/debug/DangerDebugOverlay";
import { ProfileQuickActions } from "@/components/profile/ProfileQuickActions";
import { useAuth } from "@/hooks/useAuth";
import { LogOut } from "lucide-react";
import { LiveNarrativeTicker } from "@/components/match/LiveNarrativeTicker";
import { HypeMeterButton } from "@/components/match/HypeMeterButton";
import { SpoilerSyncSlider } from "@/components/match/SpoilerSyncSlider";
import { PublicEmotionalSignalOverlay } from "@/components/public/PublicEmotionalSignalOverlay";
import { getExperienceMode, subscribeExperienceMode, type ExperienceMode } from "@/lib/pr36e/experienceModeEngine";
import {
  recordFanEvent,
  advisePredictionTiming,
  useAdaptiveFanProfile,
} from "@/lib/adaptiveFan/adaptiveFanProfile";
import { RetentionFab } from "@/components/retention/RetentionFab";
import { ShareButton } from "@/components/viral/ShareButton";
import { AttentionYieldGroup } from "@/components/orchestration/AttentionYieldGroup";
import { detectBigMoment, onBigMoment } from "@/lib/bigMoments/bigMomentsEngine";
import { useGeoCountry } from "@/hooks/useGeoCountry";
import {
  useHasFirstEngagement,
  reportSurfaceDeferred,
  reportSurfaceBelowFold,
} from "@/lib/home/firstEngagement";
import { claimPendingPrediction, PENDING_XP_AMOUNT } from "@/lib/predictions/guestPrediction";
// Heavy / conditional / overlay surfaces are loaded lazily below to keep
// the Home initial bundle small. See PR-SCALE-02.

const ProfessionalRadar3D = lazy(() =>
  import("@/components/radar/ProfessionalRadar3D").then((m) => ({ default: m.ProfessionalRadar3D })),
);

const TacticalQuickStartGuide = lazy(() =>
  import("@/components/onboarding/TacticalQuickStartGuide").then((m) => ({
    default: m.TacticalQuickStartGuide,
  })),
);

// PR-SCALE-02 — Lazy-loaded surfaces. Each is rendered conditionally
// (modal / overlay / live-only) so the initial Home bundle does not pay
// for their code, deps or styles until the user actually opens them.
const PredictionHubSheet = lazy(() =>
  import("@/components/predictions/PredictionHubSheet").then((m) => ({ default: m.PredictionHubSheet })),
);
const GoalCelebration = lazy(() =>
  import("@/components/demo/GoalCelebration").then((m) => ({ default: m.GoalCelebration })),
);
const GoalMomentBoost = lazy(() =>
  import("@/components/viral/GoalMomentBoost").then((m) => ({ default: m.GoalMomentBoost })),
);
const CinematicMatchRecap = lazy(() =>
  import("@/components/match/CinematicMatchRecap").then((m) => ({ default: m.CinematicMatchRecap })),
);
const DemoControlPanel = lazy(() =>
  import("@/components/demo/DemoControlPanel").then((m) => ({ default: m.DemoControlPanel })),
);
const RewardsCatalog = lazy(() =>
  import("@/components/gamification/RewardsCatalog").then((m) => ({ default: m.RewardsCatalog })),
);
const HalftimeEngagementHub = lazy(() =>
  import("@/components/engagement/HalftimeEngagementHub").then((m) => ({ default: m.HalftimeEngagementHub })),
);
const AdvancedStatsModal = lazy(() =>
  import("@/components/stats/AdvancedStatsModal").then((m) => ({ default: m.AdvancedStatsModal })),
);
const FanDnaCard = lazy(() =>
  import("@/components/social/FanDnaCard").then((m) => ({ default: m.FanDnaCard })),
);
const AutoViralMomentCapsule = lazy(() =>
  import("@/components/viral/AutoViralMomentCapsule").then((m) => ({ default: m.AutoViralMomentCapsule })),
);
const GlobalTakeoverCapsule = lazy(() =>
  import("@/components/global/GlobalTakeoverCapsule").then((m) => ({ default: m.GlobalTakeoverCapsule })),
);
const EmotionalRibbonOrchestrator = lazy(() =>
  import("@/components/orchestration/EmotionalRibbonOrchestrator").then((m) => ({ default: m.EmotionalRibbonOrchestrator })),
);
const PrestigeCelebrationToast = lazy(() =>
  import("@/components/global/PrestigeCelebrationToast").then((m) => ({ default: m.PrestigeCelebrationToast })),
);
const BrasilHub = lazy(() =>
  import("@/components/match/brasil/BrasilHub").then((m) => ({ default: m.BrasilHub })),
);
const MundialPredictionFloatingCard = lazy(() =>
  import("@/components/predictions/MundialPredictionFloatingCard").then((m) => ({ default: m.MundialPredictionFloatingCard })),
);
const SponsoredReactionCard = lazy(() =>
  import("@/components/commercial/SponsoredReactionCard").then((m) => ({ default: m.SponsoredReactionCard })),
);
const SocialPulseBanner = lazy(() =>
  import("@/components/social/SocialPulseBanner").then((m) => ({ default: m.SocialPulseBanner })),
);

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "VerseSport™ — Second Screen World Cup Experience" },
      {
        name: "description",
        content:
          "Premium second screen experience for the 2026 World Cup. 3D tactical radar, AI analyst chat, predictions and fan insights.",
      },
      { property: "og:title", content: "VerseSport™" },
      {
        property: "og:description",
        content: "The premium second-screen platform for the 2026 World Cup.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://versesport.com/" },
      { property: "og:image", content: "https://versesport.com/og/mundial-2026.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "VerseSport™ — Mundial 2026" },
      { name: "twitter:description", content: "The premium second-screen platform for the 2026 World Cup." },
      { name: "twitter:image", content: "https://versesport.com/og/mundial-2026.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://versesport.com/" }],
  }),
  component: Index,
});

function Index() {
  // PUBLIC_V1 — Home is publicly accessible. Unauthenticated visitors see
  // the Home shell and can explore non-live features (Nation Wars, Quinielas,
  // calendar, social, etc.). Actions requiring auth render inline CTAs.
  // /welcome remains available as a dedicated landing/onboarding route.
  return <HomeExperience />;
}

function HomeExperience() {
  const [elapsed, setElapsed] = useState(0);
  const { lang, t } = useLanguage();
  const isMobile = useIsMobile();
  const { isAuthenticated, authReady, logout, user } = useAuth();
  const navigate = useNavigate();
  // PR-40C — legacy mock prediction UI (PredictionCard /
  // PredictionHubSheet) is gated behind a runtime flag (OFF by default).
  const legacyMocksEnabled = useFlag("legacyPredictionMocks");
  // PR-30 — Claim guest prediction once the visitor lands on Home as an
  // authenticated user. Pure additive: surfaces a toast + clears the
  // pending payload so XP isn't double-claimed on subsequent visits.
  useEffect(() => {
    if (!authReady || !isAuthenticated) return;
    const claimed = claimPendingPrediction();
    if (claimed) {
      toast.success(`✨ Tu predicción fue guardada · +${PENDING_XP_AMOUNT} XP reclamados`);
    }
  }, [authReady, isAuthenticated]);
  const cleanMode = useCleanMode();
  const keyboardOpen = useKeyboardOpen();
  const ctaContext = useActiveCtaContext();
  useEffect(() => {
    const visible = !cleanMode && !keyboardOpen;
    setLayerActive("floating_cta", visible);
    return () => setLayerActive("floating_cta", false);
  }, [cleanMode, keyboardOpen]);
  // Will register CTA contexts based on local state below.
  const buildInitialChat = useCallback((home: string, away: string): ChatMessage[] => [
    { id: "m1", role: "ai", content: t("intro_msg_pressing", { home }), at: 0 },
    { id: "m2", role: "user", content: t("intro_msg_user_q"), at: 6 },
    { id: "m3", role: "ai", content: t("intro_msg_momentum"), at: 10 },
  ], [t]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [showPredictionHub, setShowPredictionHub] = useState(false);
  // Auto-open Prediction Hub when arriving with ?open=predictions
  // (used by Nation Wars "Participar en predicciones" CTA when a live
  // match is active — opens the hub directly on top of the match viewer).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("open") === "predictions") {
      setShowPredictionHub(true);
      void trackEvent("participate_cta_resolved", {
        source: "url_open_param",
        kind: "match_viewer",
        reason: "auto_open",
        to: "/",
      });
    }
  }, []);
  const [showGoal, setShowGoal] = useState(false);
  // F3 (PR-HARDENING-01) — single tracked timer for "auto-hide goal flash".
  // Replaces 3 fire-and-forget setTimeout calls that leaked across unmounts
  // and could call setShowGoal(false) on a dead tree.
  const goalHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleHideGoal = useCallback((ms: number) => {
    if (goalHideTimerRef.current) clearTimeout(goalHideTimerRef.current);
    goalHideTimerRef.current = setTimeout(() => {
      goalHideTimerRef.current = null;
      setShowGoal(false);
    }, ms);
  }, []);
  useEffect(() => () => {
    if (goalHideTimerRef.current) clearTimeout(goalHideTimerRef.current);
    goalHideTimerRef.current = null;
  }, []);
  const [tab, setTab] = useState<MobileTab>("radar");
  const [xray, setXray] = useState<{ goalPct: number; keyPassPct: number; defenderRiskHigh: boolean; visible: boolean }>({
    goalPct: 0, keyPassPct: 0, defenderRiskHigh: false, visible: false,
  });
  const logoClicks = useRef<{ count: number; last: number }>({ count: 0, last: 0 });
  const startedAt = useRef(performance.now());
  const sentMsgIds = useRef<Set<number>>(new Set());
  const onboarding = useOnboarding();
  const halftimeShown = useRef(false);
  const multiMatch = useMultiMatch();
  useRegisterCtaContext("prediction", showPredictionHub);
  const commentaryEngine = useRef(new LiveCommentaryEngine());
  const halftimeTriggered = useRef(false);
  const geo = useGeoCountry();
  // HOME-SIMPLIFICATION P1 — gate cold-start surfaces behind the
  // first guest engagement so empty "0 / —" panels never render.
  const engaged = useHasFirstEngagement();
  useEffect(() => {
    if (!engaged) {
      reportSurfaceBelowFold("clip_of_the_day");
      reportSurfaceBelowFold("audio_sync_edu");
    }
  }, [engaged]);
  // Match Pulse — track first goal per active match
  const [firstGoalAt, setFirstGoalAt] = useState<number | null>(null);
  const [firstGoalMinute, setFirstGoalMinute] = useState<number | null>(null);
  // Fan Data Pulse In-Game™ — tiempo activo del usuario en sesión (Phase 3).
  const sessionStartedAt = useRef<number>(Date.now());
  const [activeForMs, setActiveForMs] = useState<number>(0);
  useEffect(() => {
    const id = window.setInterval(() => {
      setActiveForMs(Date.now() - sessionStartedAt.current);
    }, 5000);
    return () => window.clearInterval(id);
  }, []);
  const hub = useMatchHub(elapsed, {
    enabled: multiMatch.enabled,
    followed: multiMatch.followed,
    muted: multiMatch.muted,
    onActiveMatchChange: (toId) => {
      startedAt.current = performance.now();
      sentMsgIds.current.clear();
      setActiveRoster(toId);
      const roster = getMatchRoster(toId);
      setMessages(buildInitialChat(roster.homeTeam, roster.awayTeam));
      setShowGoal(false);
      commentaryEngine.current.reset();
      halftimeTriggered.current = false;
      halftimeShown.current = false;
      setFirstGoalAt(null);
      setFirstGoalMinute(null);
      void trackEvent("active_match_loaded", { matchId: toId });
      void trackEvent("simulation_regenerated_for_match", { matchId: toId });
      void trackEvent("ai_context_bound_to_active_match", { matchId: toId });
      void trackEvent("ai_context_bound_to_roster", { matchId: toId });
      notify({
        title: t("match_switched_title", { match: toId.toUpperCase() }),
        body: t("match_switched_body"),
        tone: "info",
      });
    },
  });

  // Real Data Phase 1: stats for the active match only.
  const realMatchDetailsEnabled = useRealMatchDetailsEnabled();
  // Resolve real provider fixture ids for every match (via API-Football H2H).
  // Synthetic/demo matches keep their existing id and continue to flow through
  // DemoProvider, preserving the "Demo data" badge.
  const providerFixtures = useProviderFixtureIds(realMatchDetailsEnabled);
  const resolvedProviderFixtureId =
    providerFixtures[hub.activeId]?.providerFixtureId ??
    hub.activeMatch.providerFixtureId ??
    null;
  const fixtureIdForStats =
    realMatchDetailsEnabled
      ? (resolvedProviderFixtureId != null
          ? String(resolvedProviderFixtureId)
          : undefined)
      : undefined;
  const matchInsights = useMatchInsights(fixtureIdForStats);
  // Once we have an official provider fixture id, the match is driven by
  // real telemetry (score, minute, events). Suppress every simulation-only
  // overlay/trigger (fake GOAL banner, danger flashes, free-kick alerts,
  // xG-based clean mode) so nothing leaks from the demo frame engine.
  const hasOfficialTelemetry = !!fixtureIdForStats;
  // Safety: if official telemetry becomes available while a simulation-driven
  // GOAL banner is on screen, tear it down immediately.
  useEffect(() => {
    if (hasOfficialTelemetry) {
      if (goalHideTimerRef.current) clearTimeout(goalHideTimerRef.current);
      goalHideTimerRef.current = null;
      setShowGoal(false);
    }
  }, [hasOfficialTelemetry]);
  useEffect(() => {
    if (!realMatchDetailsEnabled) return;
    // Debug visibility for internal testers: which fixture id MatchStats uses
    // and whether the returned payload came from the real API or DemoProvider.
    console.info("[MATCH_STATS_FIXTURE]", {
      activeId: hub.activeId,
      resolvedProviderFixtureId,
      fixtureIdForStats,
      isDemo: matchInsights.isDemo,
    });
  }, [
    realMatchDetailsEnabled,
    hub.activeId,
    resolvedProviderFixtureId,
    fixtureIdForStats,
    matchInsights.isDemo,
  ]);
  const fanPoints = useFanPoints();
  const [rewardsOpen, setRewardsOpen] = useState(false);
  const [mode] = useMode();
  const isBetting = mode === "betting";
  const [bonus, setBonus] = useState<SmartBonusOffer | null>(null);
  const [halftimeOpen, setHalftimeOpen] = useState(false);
  const [hubTrigger, setHubTrigger] = useState<HubTrigger>("halftime");
  const [advStatsOpen, setAdvStatsOpen] = useState(false);
  // Contextual prediction card — coexists with the PredictionHubSheet
  // but only one is visible at a time (mutual exclusion below).
  const [predictionCard, setPredictionCard] = useState<{ q: string; id: string } | null>(null);
  useEffect(() => onOpenAdvancedStats(() => setAdvStatsOpen(true)), []);

  const lastPredictionMinute = useRef<number>(-1);
  const fanProfile = useAdaptiveFanProfile();
  const predictionTiming = useMemo(
    () => advisePredictionTiming(fanProfile),
    [fanProfile],
  );

  // Record signals into the adaptive fan profile when major surfaces open.
  useEffect(() => { if (advStatsOpen) recordFanEvent("stats_open"); }, [advStatsOpen]);
  useEffect(() => { if (halftimeOpen) recordFanEvent("insights_open"); }, [halftimeOpen]);
  useEffect(() => { if (showPredictionHub) recordFanEvent("social_open"); }, [showPredictionHub]);
  useEffect(() => { recordFanEvent("session_start"); }, []);

  // Manual halftime hub open via global event (FAB / external buttons).
  // Manual triggers bypass the auto-open policy by design.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ trigger?: HubTrigger }>).detail;
      setHubTrigger(detail?.trigger ?? "halftime");
      setHalftimeOpen(true);
      void trackEvent("mobile_halftime_hub_opened", { surface: "manual_event" });
    };
    window.addEventListener("ss2026:halftime-open", handler as EventListener);
    return () => window.removeEventListener("ss2026:halftime-open", handler as EventListener);
  }, []);

  // PR-18 Phase 7 — wire Daily Feed CTAs to actual UI actions + audit.
  useEffect(() => {
    const openRewards = () => { setRewardsOpen(true); void trackEvent("daily_feed_action_executed", { target: "rewards" }); };
    const openHalftime = () => { setHubTrigger("halftime"); setHalftimeOpen(true); void trackEvent("daily_feed_action_executed", { target: "halftime" }); };
    const openStats = () => { setAdvStatsOpen(true); void trackEvent("daily_feed_action_executed", { target: "stats" }); };
    const openTrivia = () => { setHubTrigger("halftime"); setHalftimeOpen(true); void trackEvent("daily_feed_action_executed", { target: "trivia" }); };
    const openFantasy = () => { setHubTrigger("halftime"); setHalftimeOpen(true); void trackEvent("daily_feed_action_executed", { target: "fantasy" }); };
    const openOracle = () => { setHubTrigger("halftime"); setHalftimeOpen(true); void trackEvent("daily_feed_action_executed", { target: "oracle" }); };
    const focusLive = () => { void trackEvent("daily_feed_action_executed", { target: "live" }); };
    window.addEventListener("ss2026:open-rewards", openRewards);
    window.addEventListener("ss2026:open-halftime", openHalftime);
    window.addEventListener("ss2026:open-stats", openStats);
    window.addEventListener("ss2026:open-trivia", openTrivia);
    window.addEventListener("ss2026:open-fantasy", openFantasy);
    window.addEventListener("ss2026:open-oracle", openOracle);
    window.addEventListener("ss2026:focus-live", focusLive);
    return () => {
      window.removeEventListener("ss2026:open-rewards", openRewards);
      window.removeEventListener("ss2026:open-halftime", openHalftime);
      window.removeEventListener("ss2026:open-stats", openStats);
      window.removeEventListener("ss2026:open-trivia", openTrivia);
      window.removeEventListener("ss2026:open-fantasy", openFantasy);
      window.removeEventListener("ss2026:open-oracle", openOracle);
      window.removeEventListener("ss2026:focus-live", focusLive);
    };
  }, []);

  // app_open tracking
  useEffect(() => {
    trackEvent("app_open", { ua: typeof navigator !== "undefined" ? navigator.userAgent : "" });
    void trackEvent("live_tracker_enabled", { source: "auto", players: 22 });
    void bootstrapSession();
    void captureIncomingReferral();
  }, []);

  useIpProtection(isIpProtectionRequested());

  useDemoTrigger((trig) => {
    if (trig === "restart" || trig === "start") {
      startedAt.current = performance.now();
      sentMsgIds.current.clear();
      const roster = getMatchRoster(hub.activeId);
      setMessages(buildInitialChat(roster.homeTeam, roster.awayTeam));
      toast.success(t("demo_restarted"));
    } else if (trig === "goal" || trig === "celebration") {
      if (!hasOfficialTelemetry) {
        setShowGoal(true);
        scheduleHideGoal(20000);
      }
    } else if (trig === "prediction" || trig === "survey") {
      setShowPredictionHub(true);
    }
  });

  // simulation clock
  useEffect(() => {
    let raf = 0;
    let lastPush = 0;
    // FRONTEND INTEGRATION RECOVERY — FASE 8 (motion regression).
    // Throttle the route-level state churn from 60Hz to ~12Hz. The radar
    // does its own internal interpolation, so we only need a coarse tick
    // for chat scheduling, danger evaluation and overlays. Re-rendering the
    // entire route 60 times/sec produced jitter and "camera shake" because
    // every child (timeline, ads, hub, narrative) re-computed each frame.
    const STEP_MS = 80;
    const tick = () => {
      const t = (performance.now() - startedAt.current) / 1000;
      const now = performance.now();
      if (now - lastPush >= STEP_MS) {
        lastPush = now;
        setElapsed(t % 120);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // schedule chat & events based on elapsed seconds
  const intElapsed = Math.floor(elapsed);
  const scheduledAiMessages = useMemo(() => {
    const r = getMatchRoster(hub.activeId);
    const home = r.homeTeam;
    const away = r.awayTeam;
    const star = r.homePlayers[8]?.name ?? home;
    return [
      { at: 22, content: t("sched_msg_retreat", { away, player: star }) },
      { at: 35, content: t("sched_msg_possession", { home }) },
      { at: 50, content: t("sched_msg_hot_zone") },
      { at: 62, content: t("sched_msg_goal") },
      { at: 85, content: t("sched_msg_control", { home, away }) },
      { at: 110, content: t("sched_msg_added_time") },
    ];
  }, [hub.activeId, t]);

  useEffect(() => {
    // Context-aware prediction rotation
    // RHYTHM REBALANCE — más disparos para restaurar la sensación de
    // votación comunitaria continua sin saturar la cancha.
    const triggers: { at: number; pickIds: string[] }[] = [
      { at: 8,  pickIds: ["next_goal_2min", "shot_on_target"] },
      { at: 18, pickIds: ["shot_on_target", "corner_kick"] },
      { at: 28, pickIds: ["yellow_card", "next_goal_2min"] },
      { at: 40, pickIds: ["corner_kick", "yellow_card"] },
      { at: 52, pickIds: ["shot_on_target", "next_goal_2min"] },
      { at: 70, pickIds: ["sub_before_70", "var_review"] },
      { at: 82, pickIds: ["yellow_card", "corner_kick"] },
      { at: 95, pickIds: ["red_card", "shot_on_target"] },
      { at: 105, pickIds: ["next_goal_2min", "yellow_card"] },
    ];
    const trig = triggers.find((x) => x.at === intElapsed);
    if (trig) void trackEvent("prediction_view", { surface: "market_moment", at: intElapsed });
    // Goal at t=60
    if (intElapsed === 60 && !showGoal && !hasOfficialTelemetry) {
      setShowGoal(true);
      scheduleHideGoal(20000);
    }
    // scheduled AI messages
    scheduledAiMessages.forEach((m, idx) => {
      if (intElapsed === m.at && !sentMsgIds.current.has(idx)) {
        sentMsgIds.current.add(idx);
        setMessages((prev) => [
          ...prev,
          { id: `auto-${idx}-${Date.now()}`, role: "ai", content: m.content, at: intElapsed },
        ]);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intElapsed]);

  const frame = useMemo(() => getProFrame(elapsed, 0.05, "snapshot"), [elapsed]);
  const adEngine = useAdEngine(frame, elapsed);

  // Live AI commentary tick — context aware, uses player names from frame
  useEffect(() => {
    const c = commentaryEngine.current.tick(
      {
        t: elapsed,
        minute: frame.minute,
        ball: frame.ball,
        players: frame.players,
        inDanger: frame.inDanger,
        isGoal: frame.isGoal,
        freeKick: frame.freeKick,
        attackingMex: frame.attackingMex,
        momentum: frame.momentum,
        xgPct: xray.goalPct,
        homeCode: hub.activeMatch.homeCode,
        awayCode: hub.activeMatch.awayCode,
      },
      Date.now(),
    );
    if (c) {
      setMessages((prev) => [
        ...prev,
        { id: c.id, role: "ai", content: c.text, at: Math.floor(elapsed) },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Math.floor(elapsed)]);

  // Smart bonus evaluator — betting demo only
  useEffect(() => {
    if (!isBetting) return;
    if (bonus) return;
    const rg = isResponsibleGamingAllowed();
    const offer = evaluateSmartBonus(frame, elapsed, {
      city: "Miami",
      matchId: hub.activeId,
      responsibleGamingAllowed: rg,
    });
    if (offer) setBonus(offer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Math.floor(elapsed), isBetting]);

  // Engagement Hub triggers — halftime, commercial breaks, VAR, post-match
  useEffect(() => {
    const e = Math.floor(elapsed);
    if (e === 55 && !halftimeTriggered.current) {
      halftimeTriggered.current = true;
      if (
        evaluateAutoOpen("halftime").allow &&
        allowAutoOpen("halftime", "neutral", { minute: e })
      ) {
        setHubTrigger("halftime");
        setHalftimeOpen(true);
      }
    } else if (e === 90 && !halftimeTriggered.current) {
      // post-match catch-all if halftime didn't fire
      halftimeTriggered.current = true;
      if (
        evaluateAutoOpen("post_match").allow &&
        allowAutoOpen("post_match", "neutral", { minute: e })
      ) {
        setHubTrigger("post_match");
        setHalftimeOpen(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Math.floor(elapsed)]);

  // Mark goal sequence in audit
  useEffect(() => {
    if (frame.isGoal && !hasOfficialTelemetry) void trackEvent("simulation_goal_sequence", { minute: frame.minute });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frame.isGoal]);

  // Match Pulse — capture first goal moment (for the 15s post-goal trigger)
  useEffect(() => {
    if (frame.isGoal && firstGoalAt === null && !hasOfficialTelemetry) {
      setFirstGoalAt(Date.now());
      setFirstGoalMinute(frame.minute);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frame.isGoal]);

  // PR-DISCOVERY-01 · P0-1 — Wire the premium goal celebration to real goal
  // events. Fires once per `showGoal` rising edge with the live team/nation
  // context from `matchCtx`. The PremiumGoalCelebration overlay (mounted
  // globally inside RetentionFab) auto-dismisses after ~2.6s.
  useEffect(() => {
    if (!showGoal) return;
    firePremiumGoal({
      nationCode: matchCtx.homeCode ?? null,
      teamLabel: matchCtx.homeTeamName ?? matchCtx.homeCode ?? null,
      sound: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGoal]);

  // Auto Goal Clean Mode — hide overlays during goals, shots on target, and free kicks near box
  useEffect(() => {
    if (showGoal) activateGoalCleanMode("goal", 5000);
  }, [showGoal]);
  useEffect(() => {
    if (hasOfficialTelemetry) return;
    if (xray.goalPct >= 0.55) activateGoalCleanMode("shot_on_target", 4000);
    if (xray.goalPct >= 0.55) activateDangerPlay("xg_high", 5000);
  }, [xray.goalPct >= 0.55]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (hasOfficialTelemetry) return;
    if (frame.freeKick && frame.ball.x > 18) activateGoalCleanMode("shot_on_target", 4000);
    if (frame.freeKick && frame.ball.x > 18) activateDangerPlay("free_kick_dangerous", 5000);
  }, [frame.freeKick]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (hasOfficialTelemetry) return;
    if (frame.inDanger) activateDangerPlay("dangerous_attack", 4000);
  }, [frame.inDanger]); // eslint-disable-line react-hooks/exhaustive-deps
  // Predictive threat window — sampled at ~5Hz from the live sim feed.
  // This is the REAL Goal Protection driver: it engages BEFORE the shot
  // by blending momentum, final-third ball position, sprinting count,
  // inDanger and xG into a single pressure score.
  const threatTick = Math.floor(elapsed * 5);
  useEffect(() => {
    const sprintingCount = frame.players.reduce((n, p) => n + (p.sprinting ? 1 : 0), 0);
    evaluateThreatWindow({
      momentum: frame.momentum,
      ballX: frame.ball.x,
      inDanger: frame.inDanger,
      isShot: frame.isShot,
      isGoal: frame.isGoal,
      freeKick: frame.freeKick,
      xgPct: xray.goalPct,
      sprintingCount,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threatTick]);
  // Audit: emit once whenever the floating CTA becomes hidden because the
  // keyboard opens or Goal Clean Mode is active (prevents send-button overlap).
  useEffect(() => {
    if (cleanMode || keyboardOpen) {
      void trackEvent("send_button_visibility_fixed", {
        reason: cleanMode ? "clean_mode" : "keyboard_open",
      });
    }
  }, [cleanMode, keyboardOpen]);

  useEffect(() => {
    if (frame.players.some((p) => p.sprinting)) {
      // throttle: emit once per second
      const k = Math.floor(elapsed);
      if (k % 2 === 0) void trackEvent("simulation_sprint_event", { t: k });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Math.floor(elapsed / 2)]);

  const handleSend = useCallback((text: string) => {
    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: "user",
      content: text,
      at: Math.floor(elapsed),
    };
    setMessages((prev) => [...prev, userMsg]);
    void fanPoints.award("chat_message", { surface: "chat" });
  }, [elapsed, fanPoints]);

  const handleAiReply = useCallback(
    (content: string) => {
      setMessages((prev) => [
        ...prev,
        { id: `ai-${Date.now()}`, role: "ai", content, at: Math.floor(elapsed) },
      ]);
    },
    [elapsed],
  );

  const handleAudioSync = useCallback((target: number) => {
    // Shift simulation clock so elapsed jumps to `target` seconds
    startedAt.current = performance.now() - target * 1000;
    sentMsgIds.current.clear();
  }, []);

  const handleLogoClick = () => {
    const now = Date.now();
    const prev = logoClicks.current;
    if (now - prev.last < 600) prev.count += 1;
    else prev.count = 1;
    prev.last = now;
    if (prev.count >= 3) {
      prev.count = 0;
      // restart sim
      startedAt.current = performance.now();
      sentMsgIds.current.clear();
      const r = getMatchRoster(hub.activeId);
      setMessages(buildInitialChat(r.homeTeam, r.awayTeam));
      if (!hasOfficialTelemetry) {
        setShowGoal(true);
        scheduleHideGoal(20000);
      }
      toast.success(t("demo_mode_activated_title"), { description: t("demo_mode_activated_sub") });
    }
  };

  const handleParticipate = () => {
    trackEvent("participate_click", { surface: "floating_cta" });
    trackEvent("survey_response", {
      survey_id: "participate_cta",
      surface: "floating_cta",
      country: "US",
      city: "Miami",
    });
    trackEvent("prediction_view", { surface: "floating_cta" });
    setShowPredictionHub(true);
  };

  // PR-27 — make the Predictions FAB user-draggable so it never blocks the
  // scoreboard, radar or sync controls on mobile. Position persists locally.
  const predictionsFab = useDraggableFab("participate_fab");

  // Active match-aware scoreboard — single source of truth via getActiveMatchContext.
  const activeSnap = hub.snapshots[hub.activeId];
  const isDefaultMatch = hub.activeId === "mex-arg";
  const shouldUseFrameFallback = isDefaultMatch && !fixtureIdForStats;
  const matchCtx = useMemo(
    () => getActiveMatchContext(
      hub.activeMatch,
      activeSnap,
      shouldUseFrameFallback ? { homeScore: frame.scoreMex, awayScore: frame.scoreArg, minute: frame.minute } : undefined,
    ),
    [hub.activeMatch, activeSnap, shouldUseFrameFallback, frame.scoreMex, frame.scoreArg, frame.minute],
  );
  const headerHomeScore = matchCtx.homeScore;
  const headerAwayScore = matchCtx.awayScore;
  const headerMinute = matchCtx.minute;

  useEffect(() => {
    if (matchCtx.status === "LIVE" || engaged) return;
    reportSurfaceDeferred("nation_position_badge");
    reportSurfaceDeferred("day2_return_panel");
    reportSurfaceDeferred("nation_momentum_line");
    reportSurfaceDeferred("geo_dynamic_banner");
    reportSurfaceDeferred("next_mission_card");
  }, [matchCtx.status, engaged]);

  // Contextual prediction prompts — fire every 5 in-match minutes,
  // skipping when another engagement surface is already on screen.
  useEffect(() => {
    const minute = matchCtx.minute;
    if (typeof minute !== "number" || minute <= 0) return;
    if (predictionCard || showPredictionHub || halftimeOpen) return;
    if (cleanMode || keyboardOpen) return;
    if (minute === lastPredictionMinute.current) return;
    if (minute % predictionTiming.interval !== 0) return;
    lastPredictionMinute.current = minute;
    const home = matchCtx.homeTeamName ?? matchCtx.homeCode;
    const away = matchCtx.awayTeamName ?? matchCtx.awayCode;
    const tacticalPool = [
      { q: `¿${home} domina los próximos 5 minutos?`, id: `ctx_dom_${minute}` },
      { q: `¿Próxima tarjeta amarilla para ${away}?`, id: `ctx_card_${minute}` },
      { q: `¿Habrá tiro al arco en los próximos 3 minutos?`, id: `ctx_shot_${minute}` },
    ];
    const emotionalPool = [
      { q: `¿Habrá gol antes del minuto ${Math.min(90, minute + 10)}?`, id: `ctx_goal_${minute}` },
      { q: `¿Sientes que ${home} va por todo ahora?`, id: `ctx_hype_${minute}` },
      { q: `¿Llega un momento épico en los próximos 5'?`, id: `ctx_epic_${minute}` },
    ];
    const socialPool = [
      { q: `¿La hinchada de ${home} explota en 5'?`, id: `ctx_crowd_${minute}` },
      { q: `¿Tu predicción vence al ranking global?`, id: `ctx_rank_${minute}` },
      { q: `¿${home} mantiene la ventaja al minuto ${minute + 5}?`, id: `ctx_hold_${minute}` },
    ];
    const balancedPool = [...tacticalPool, ...emotionalPool, ...socialPool];
    const pool =
      predictionTiming.pool === "tactical"  ? tacticalPool  :
      predictionTiming.pool === "emotional" ? emotionalPool :
      predictionTiming.pool === "social"    ? socialPool    :
                                              balancedPool;
    setPredictionCard(pool[minute % pool.length]);
  }, [matchCtx.minute, matchCtx.homeTeamName, matchCtx.awayTeamName, matchCtx.homeCode, matchCtx.awayCode, predictionCard, showPredictionHub, halftimeOpen, cleanMode, keyboardOpen, predictionTiming.interval, predictionTiming.pool]);

  const matchClock = useMemo(
    () => deriveMatchClock(headerMinute, matchCtx.status),
    [headerMinute, matchCtx.status],
  );
  const aiTemporalContext = useMemo(() => buildAiTemporalContext(matchClock), [matchClock]);
  const richCtx = useMemo(
    () => buildRichMatchContext(hub.activeMatch, activeSnap, matchCtx),
    [hub.activeMatch, activeSnap, matchCtx],
  );
  const richAiContext = useMemo(
    () => formatRichContextForAi(matchCtx, richCtx),
    [matchCtx, richCtx],
  );

  useEffect(() => {
    setActiveRoster(hub.activeId);
    const roster = getMatchRoster(hub.activeId);
    setMessages((prev) => prev.length ? prev : buildInitialChat(roster.homeTeam, roster.awayTeam));
  }, [hub.activeId, buildInitialChat]);

  useEffect(() => {
    void trackEvent("active_match_data_source_verified", { matchId: matchCtx.id });
  }, [matchCtx.id]);

  // ─────────────────────────────────────────────────────────────
  // BIG MOMENTS — pure detection from existing telemetry.
  // Watches scoreboard + danger + minute; fires window event that
  // BigMomentFlash, prediction card, and dangerPlay all react to.
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof matchCtx.minute !== "number") return;
    detectBigMoment({
      minute: matchCtx.minute,
      scoreHome: matchCtx.homeScore ?? 0,
      scoreAway: matchCtx.awayScore ?? 0,
      homeLabel: matchCtx.homeTeamName ?? matchCtx.homeCode ?? "Local",
      awayLabel: matchCtx.awayTeamName ?? matchCtx.awayCode ?? "Visitante",
      inDanger: frame.inDanger,
      momentum: typeof frame.momentum === "number" ? frame.momentum : 0,
    });
  }, [
    matchCtx.homeScore,
    matchCtx.awayScore,
    matchCtx.minute,
    matchCtx.homeTeamName,
    matchCtx.awayTeamName,
    matchCtx.homeCode,
    matchCtx.awayCode,
    frame.inDanger,
    frame.momentum,
  ]);

  // BIG MOMENT REACTIONS — fan participation triggers + danger boost.
  useEffect(() => {
    return onBigMoment((m) => {
      // Fire a contextual prediction card if nothing is currently up.
      if (
        m.predictionQuestion &&
        !predictionCard &&
        !showPredictionHub &&
        !halftimeOpen &&
        !advStatsOpen &&
        !cleanMode &&
        !keyboardOpen
      ) {
        setPredictionCard({ q: m.predictionQuestion, id: `bm_${m.id}` });
      }
      // Hi-intensity moments boost danger play / momentum vibration.
      if (m.intensity >= 0.85) {
        try {
          activateDangerPlay(
            m.kind === "penalty" ? "penalty"
            : m.kind === "var" ? "var_goal_check"
            : "imminent_shot",
            4000,
          );
        } catch { /* noop */ }
      }
      void trackEvent("big_moment_reaction_fired", { kind: m.kind });
    });
  }, [predictionCard, showPredictionHub, halftimeOpen, advStatsOpen, cleanMode, keyboardOpen]);

  // EXPERIENCE POLISH — propagate Emotional/Pro mode to the document so CSS
  // can flip glow / animation density without per-component plumbing.
  const [expMode, setExpMode] = useState<ExperienceMode>(() => {
    try { return getExperienceMode(); } catch { return "emotional"; }
  });
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.experienceMode = expMode;
    return () => { delete document.documentElement.dataset.experienceMode; };
  }, [expMode]);
  useEffect(() => subscribeExperienceMode(setExpMode), []);

  // Narrative ticker sample key — 1 Hz is enough; engine throttles to 15-25s.
  const narrativeSampleKey = Math.floor(elapsed);

  useEffect(() => {
    void trackEvent("sports_data_canonicalization_completed", {
      matchId: matchCtx.id,
      homeTeam: matchCtx.homeTeamName,
      awayTeam: matchCtx.awayTeamName,
      topPerformer: matchCtx.topPerformer.player.name,
    });
    void trackEvent("advanced_stats_globalized", { matchId: matchCtx.id });
    void trackEvent("promotion_labels_localized", { lang });
  }, [matchCtx.id, matchCtx.homeTeamName, matchCtx.awayTeamName, matchCtx.topPerformer.player.name, lang]);

  // Verify i18n full reactivity once per language change
  useEffect(() => {
    void trackEvent("i18n_full_reactivity_verified", { lang });
    void trackEvent("i18n_zero_hardcoded_strings_verified", { lang });
    void trackEvent("i18n_final_cleanup_completed", { lang });
  }, [lang]);

  // PUBLIC_DEMO_DATA_GATE — hide synthetic/demo matches from normal users.
  // Demo mode is allowed for superadmins or when the URL carries ?mode=demo.
  const demoAllowed = useDemoModeAllowed();
  // PUBLIC_INTERACTIVE_DEMO — a normal visitor can explicitly opt into the
  // simulated second-screen experience by hitting the "View interactive
  // demo" CTA, which sets `?preview=demo`. Synthetic match stays clearly
  // labelled while this is active; not equivalent to superadmin demo mode.
  const { active: previewDemo, exit: exitPreviewDemo } = usePublicDemoPreview();
  // REAL_FIXTURE_CAROUSEL — Only matches backed by a real provider fixture
  // id are shown in the public carousel/calendar. Synthetic matches keep
  // powering the pitch/radar as an "interactive preview" but are never
  // listed as if they were a real scheduled game. Privileged demo viewers
  // (superadmins, ?mode=demo, ?preview=demo) still see synthetic entries.
  const allowSynthetic = demoAllowed || previewDemo;
  // REAL_FIXTURES_AS_HOME_MATCH_SOURCE — the public Home carousel and the
  // live-unlock signal come from `unifiedSports.getFixtures()` (real
  // provider data), NOT from the synthetic MATCHES list. Synthetic matches
  // are only used to drive the pitch/radar preview when no real live
  // fixture is active. Privileged demo viewers (superadmins, ?mode=demo,
  // ?preview=demo) still see the synthetic list in the carousel.
  const realFixtures = useRealFixtureMatches();
  const liveRealMatchId = realFixtures.liveId;
  const activeIsSynthetic = hub.activeMatch?.isSynthetic === true && !fixtureIdForStats;

  // Real-fixture carousel selection (independent from the synthetic radar
  // hub.activeId — the radar stays on its synthetic match used as the
  // interactive preview). Auto-prefers the live fixture when present.
  const [selectedRealId, setSelectedRealId] = useState<string | null>(null);
  useEffect(() => {
    if (allowSynthetic) return;
    if (liveRealMatchId && selectedRealId !== liveRealMatchId) {
      setSelectedRealId(liveRealMatchId);
      return;
    }
    if (
      !selectedRealId ||
      !realFixtures.matches.some((m) => m.id === selectedRealId)
    ) {
      setSelectedRealId(realFixtures.matches[0]?.id ?? null);
    }
  }, [allowSynthetic, liveRealMatchId, realFixtures.matches, selectedRealId]);

  const visibleMatches = allowSynthetic ? hub.matches : realFixtures.matches;
  const visibleSnapshots = allowSynthetic ? hub.snapshots : realFixtures.snapshots;
  const visibleActiveId = allowSynthetic
    ? hub.activeId
    : selectedRealId ?? realFixtures.matches[0]?.id ?? "";
  const handleVisibleSelect = (id: string) => {
    if (allowSynthetic) {
      hub.switchMatch(id);
    } else {
      setSelectedRealId(id);
    }
  };
  // Also short-circuit while the redirect effect above hasn't run yet, so we
  // never flash synthetic data in the Header/MatchStats for normal users.
  const showPublicEmptyState = false;
  // RESTORE_ORIGINAL_HOME_SHELL — the original Home shell always renders.
  // Only the live scoreboard / cancha / synthetic match telemetry is gated
  // when no real fixture is available (see radar <section> below and the
  // Header / GlobalScoreBar props).

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      {/* Ambient gradient */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 0%, oklch(0.58 0.24 27 / 0.25) 0%, transparent 70%), radial-gradient(40% 40% at 90% 100%, oklch(0.82 0.24 145 / 0.12) 0%, transparent 70%)",
        }}
      />
      <DangerDebugOverlay />

      {activeIsSynthetic && (
        <DemoModeBanner
          variant={demoAllowed ? "admin" : "preview"}
          onExit={previewDemo && !demoAllowed ? exitPreviewDemo : undefined}
        />
      )}

      <Header
        scoreMex={showPublicEmptyState ? 0 : headerHomeScore}
        scoreArg={showPublicEmptyState ? 0 : headerAwayScore}
        minute={showPublicEmptyState ? 0 : headerMinute}
        matchStatus={matchCtx.status}
        isDemo={activeIsSynthetic && !fixtureIdForStats}
        providerFixtureId={fixtureIdForStats ?? null}
        homeCode={hub.activeMatch.homeCode}
        awayCode={hub.activeMatch.awayCode}
        homeFlag={hub.activeMatch.homeFlag}
        awayFlag={hub.activeMatch.awayFlag}
        onLogoClick={handleLogoClick}
        onAudioSync={handleAudioSync}
        onHelpClick={onboarding.open}
        multiMatchEnabled={multiMatch.enabled}
        onToggleMultiMatch={multiMatch.toggleEnabled}
        points={fanPoints.points}
        onOpenRewards={() => setRewardsOpen(true)}
        onOpenHalftime={() => {
          setHubTrigger("halftime");
          setHalftimeOpen(true);
          void trackEvent("mobile_halftime_hub_opened", { surface: "manual" });
        }}
      />

      {multiMatch.enabled && !showPublicEmptyState && visibleMatches.length > 0 && (
        <GlobalScoreBar
          matches={visibleMatches}
          snapshots={visibleSnapshots}
          activeId={visibleActiveId}
          onSelect={handleVisibleSelect}
          expanded={multiMatch.expanded}
          onToggleExpanded={multiMatch.toggleExpanded}
          followed={multiMatch.followed}
          onToggleFollow={multiMatch.toggleFollow}
          muted={multiMatch.muted}
          onToggleMuted={multiMatch.toggleMuted}
          isDemo={visibleMatches.length > 0 && visibleMatches.every((m) => m.isSynthetic === true)}
        />
      )}
      {multiMatch.enabled && !showPublicEmptyState && visibleMatches.length === 0 && (
        <div className="border-b border-border/40 glass">
          <div className="mx-auto max-w-7xl px-3 py-1.5 md:px-6">
            <p className="text-[11px] text-muted-foreground">
              No hay partidos programados disponibles en este momento.
            </p>
          </div>
        </div>
      )}

      {multiMatch.enabled && !multiMatch.muted && (
        <AttentionYieldGroup selfKey="cross_match">
          <CrossMatchToast
            toasts={hub.toasts}
            onOpen={hub.openToast}
            onDismiss={hub.dismissToast}
          />
        </AttentionYieldGroup>
      )}

      <main className="relative mx-auto max-w-7xl px-3 pt-4 sm:px-6 lg:pb-8 pb-[calc(112px+env(safe-area-inset-bottom))]">
        {/* WORLD-CUP-RETENTION-FIX-P0 — single hero CTA above-the-fold so a
            first-time fan sees a clear action in under 10 seconds. */}
        {/* HOME-SIMPLIFICATION P1 — DEFER until first engagement. */}
        {matchCtx.status !== "LIVE" && engaged && (
          <div className="mb-2 flex justify-center">
            <NationPositionBadge />
          </div>
        )}
        {matchCtx.status !== "LIVE" && engaged && (
          <div className="mb-3">
            <Day2ReturnPanel />
          </div>
        )}
        <IncomingChallengeBanner />
        {matchCtx.status !== "LIVE" && engaged && (
          <div className="mb-2">
            <NationMomentumLine />
          </div>
        )}
        {matchCtx.status !== "LIVE" && (
          <Link
            to="/quinielas"
            onClick={() => void trackEvent("home_hero_predict_cta_clicked", { surface: "home_hero" })}
            className="mb-3 flex items-center justify-between gap-3 rounded-2xl border border-primary/50 bg-gradient-to-r from-primary/20 via-primary/10 to-accent/15 p-4 shadow-lg transition active:scale-[0.99]"
          >
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Mundial 2026</div>
              <div className="text-base font-black leading-tight sm:text-lg">
                Predice el próximo partido en 10 segundos
              </div>
              <div className="text-[11px] text-muted-foreground">Gana puntos para tu nación · sin instalar nada</div>
            </div>
            <span className="shrink-0 rounded-full bg-primary px-4 py-2 text-xs font-black text-primary-foreground shadow">
              Predecir →
            </span>
          </Link>
        )}
        {/* HOME-SIMPLIFICATION P1 — ClipOfTheDay, AudioSyncEdu and
            AmbassadorDiscoveryBanner moved below the fold / removed.
            AmbassadorDiscoveryBanner is no longer rendered on Home;
            the experience remains accessible at /ambassadors. */}
        {matchCtx.status !== "LIVE" && engaged && (
          <div className="mb-3">
            <GeoDynamicBanner />
          </div>
        )}
        <div className="mb-3">
          <ActivationBanner surface="home" />
        </div>
        {/* HOME-QUICK-ACCESS — Perfil / Partner / Quiniela. Hidden en PREVIEW
            para maximizar el área útil de la cancha; accesos siguen
            disponibles en el menú y en la BottomNav. */}
        {!PREVIEW_MODE && (
        <div className="mb-3 grid grid-cols-3 gap-2">
          <Link
            to="/mi-identidad"
            onClick={() => void trackEvent("home_quick_access_clicked", { target: "profile" })}
            className="flex flex-col items-center justify-center gap-1 rounded-2xl border border-border/60 bg-card/60 px-2 py-3 text-center transition active:scale-[0.98] hover:bg-accent"
          >
            {(() => {
              const meta = (user?.user_metadata ?? {}) as { full_name?: string; name?: string; avatar_url?: string; picture?: string };
              const displayName = meta.full_name || meta.name || user?.email?.split("@")[0] || t("guest_label");
              const avatar = meta.avatar_url || meta.picture;
              const initial = (displayName || "?").trim().charAt(0).toUpperCase();
              return avatar ? (
                <img src={avatar} alt={displayName} referrerPolicy="no-referrer" className="h-8 w-8 rounded-full object-cover ring-2 ring-primary/40" />
              ) : (
                <div className="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-[oklch(0.58_0.24_27)] to-[oklch(0.7_0.22_15)] text-xs font-black text-white">{initial}</div>
              );
            })()}
            <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Perfil</span>
          </Link>
          <Link
            to="/partners"
            onClick={() => void trackEvent("home_quick_access_clicked", { target: "partners" })}
            className="flex flex-col items-center justify-center gap-1 rounded-2xl border border-[oklch(0.78_0.20_50)]/40 bg-gradient-to-br from-[oklch(0.78_0.20_50)]/15 to-transparent px-2 py-3 text-center transition active:scale-[0.98] hover:bg-accent"
          >
            <div className="grid h-8 w-8 place-items-center rounded-full bg-[oklch(0.78_0.20_50)]/20 text-[oklch(0.85_0.20_60)]">🤝</div>
            <span className="text-[10px] font-black uppercase tracking-widest text-[oklch(0.85_0.20_60)]">Partner</span>
          </Link>
          <Link
            to="/quinielas"
            onClick={() => void trackEvent("home_quick_access_clicked", { target: "quiniela" })}
            className="flex flex-col items-center justify-center gap-1 rounded-2xl border border-amber-400/50 bg-gradient-to-br from-amber-500/15 to-transparent px-2 py-3 text-center transition active:scale-[0.98] hover:bg-accent"
          >
            <div className="grid h-8 w-8 place-items-center rounded-full bg-amber-500/20 text-amber-300">🏆</div>
            <span className="text-[10px] font-black uppercase tracking-widest text-amber-300">Quiniela</span>
          </Link>
        </div>
        )}
        {/* PREVIEW cleanup — la cancha es protagonista. Sponsor rivalry poll
            se mueve DEBAJO del grid principal para no empujar la cancha
            del primer viewport. Fuera de PREVIEW mantiene su posición
            original. */}
        {!PREVIEW_MODE && <SponsorRivalryPoll matchId={matchCtx.id ?? null} />}
        {/* REAL_FIXTURE_CALENDAR — upcoming real matches window. Only
            renders when there is no live real fixture. En PREVIEW la
            identidad del partido ya vive dentro de la cancha, así que
            evitamos el bloque redundante para reducir espacios muertos. */}
        {!liveRealMatchId && !PREVIEW_MODE && (
          <div className="mb-3">
            <MatchCalendar />
          </div>
        )}
        {/* HOME-SIMPLIFICATION P1 — NextMissionCard DEFERRED until first
            engagement: empty mission tracker generates cold cognitive load. */}
        {engaged && (
          <div className="mb-3">
            <NextMissionCard />
          </div>
        )}
        {engaged && (
          <div className="mb-3 space-y-3">
            <ResultMomentCard />
            <StreakRecoveryCard />
          </div>
        )}
        {/* Single emotional ribbon slot — orchestrator rotates Story Arc /
            Nation Rivalry / World Status by priority. */}
        {matchCtx.status === "LIVE" && (
          <AttentionYieldGroup selfKey="story_arc" className="mb-3">
            <Suspense fallback={null}>
            <EmotionalRibbonOrchestrator
              ctx={{
                homeLabel: matchCtx.homeTeamName ?? matchCtx.homeCode ?? "Local",
                awayLabel: matchCtx.awayTeamName ?? matchCtx.awayCode ?? "Visitante",
                scoreHome: matchCtx.homeScore ?? 0,
                scoreAway: matchCtx.awayScore ?? 0,
              }}
              minute={headerMinute ?? 0}
              isLive={matchCtx.status === "LIVE"}
            />
            </Suspense>
          </AttentionYieldGroup>
        )}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-10">
          {/* FASE BRASIL P1.5 — Brasil Hub (CazéTV + Ranking + Missões + Apostas teaser). */}
          {geo.isBrazil && (
            <div className="lg:col-span-10">
              <Suspense fallback={null}>
                <BrasilHub
                  homeTeam={matchCtx.homeTeamName ?? matchCtx.homeCode ?? undefined}
                  awayTeam={matchCtx.awayTeamName ?? matchCtx.awayCode ?? undefined}
                />
              </Suspense>
            </div>
          )}
          {/* Radar */}
          <section
            className={`${PREVIEW_MODE || tab === "radar" ? "block" : "hidden"} lg:block lg:col-span-7`}
            aria-label="3D field radar"
          >
          <div className="mb-3 hidden md:block">
            <QuinielaTopCTA />
          </div>
          {showPublicEmptyState ? (
            <div className="space-y-4">
              <div
                role="status"
                aria-live="polite"
                className="rounded-2xl border-2 border-amber-500/60 bg-amber-500/10 p-5 text-center"
              >
                <div className="text-[11px] font-black uppercase tracking-[0.25em] text-amber-400">
                  Simulation / Replay — NOT LIVE
                </div>
                <div className="mt-2 text-lg font-black uppercase tracking-wide text-foreground sm:text-xl">
                  No live match right now
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  This is not a live match. The live pitch, scoreboard and
                  telemetry return when a real fixture starts.
                </p>
              </div>
              <NoLiveMatch />
            </div>
          ) : (
            <>
            {activeIsSynthetic && !demoAllowed && (
              <div className="mb-2 flex flex-col items-center justify-center gap-0.5">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/60 bg-amber-500/15 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-[0.22em] text-amber-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                  Vista previa interactiva
                </span>
                <span className="text-[10px] text-muted-foreground">
                  Se activará cuando comience un partido en vivo
                </span>
              </div>
            )}
            {/* HOME-LAYOUT-RECOVERY: in PREVIEW_MODE on mobile we drop the
                ticker + ad strip and the block-variant identity bar so the
                pitch reaches the first viewport without excessive scroll.
                COLLISION-FIX BAND — exclusive strip above the pitch.
                Left: NarrativeTicker (≤45%). Right: Sponsor.
                Never overlaps scoreboard interno / banderas / labels. */}
            <div
              className={`mb-2 min-h-[34px] flex-col items-stretch justify-between gap-2 sm:flex sm:flex-row sm:items-center ${
                PREVIEW_MODE ? "hidden sm:flex" : "flex"
              }`}
            >
              <div className="flex min-w-0 items-center sm:max-w-[45%]">
                <AttentionYieldGroup selfKey="narrative_ticker">
                  <LiveNarrativeTicker
                    minute={frame.minute}
                    momentum={frame.momentum ?? 0}
                    inDanger={frame.inDanger}
                    attackingHome={!frame.attackingMex}
                    ballX={frame.ball.x}
                    sprintingCount={frame.players.reduce((n, p) => n + (p.sprinting ? 1 : 0), 0)}
                    xgPct={xray.goalPct}
                    homeLabel={matchCtx.homeTeamName}
                    awayLabel={matchCtx.awayTeamName}
                    starName={matchCtx.topPerformer.player.name}
                    sampleKey={narrativeSampleKey}
                  />
                </AttentionYieldGroup>
              </div>
              <div className="flex min-w-0 items-center justify-end sm:max-w-[55%]">
                <RadarSmartAd
                  campaign={adEngine.current?.campaign ?? null}
                  event={adEngine.current?.event ?? null}
                  onClose={adEngine.dismiss}
                />
              </div>
            </div>
            {/* PR-UX-VIRAL-01: on mobile (≤360w) the floating overlay
                identity bar covered the 3D radar. Render it as a block
                ABOVE the pitch on mobile, overlay-only on sm+. */}
            {isMobile && !PREVIEW_MODE && (
              <PitchTeamIdentityBar
                variant="block"
                homeCode={matchCtx.homeCode}
                awayCode={matchCtx.awayCode}
                homeFlag={matchCtx.homeFlag}
                awayFlag={matchCtx.awayFlag}
                homeTeamName={matchCtx.homeTeamName}
                awayTeamName={matchCtx.awayTeamName}
                homeScore={matchCtx.homeScore}
                awayScore={matchCtx.awayScore}
                attackingDirection={frame.attackingMex ? "right" : "left"}
                minute={matchCtx.minute}
                formationHome={richCtx.formationHome}
                formationAway={richCtx.formationAway}
                topPerformerName={matchCtx.topPerformer.player.name}
                topPerformerTeam={matchCtx.topPerformer.teamName}
              />
            )}
            <div className={`pitch-team-frame relative aspect-[16/11] min-h-[260px] w-full overflow-hidden rounded-3xl border border-border glass transition-shadow ${isMobile ? "mt-2" : ""}`}>
              <div className="pitch-team-ambient" aria-hidden="true" />
              {(!isMobile || PREVIEW_MODE) && (
              <PitchTeamIdentityBar
                homeCode={matchCtx.homeCode}
                awayCode={matchCtx.awayCode}
                homeFlag={matchCtx.homeFlag}
                awayFlag={matchCtx.awayFlag}
                homeTeamName={matchCtx.homeTeamName}
                awayTeamName={matchCtx.awayTeamName}
                homeScore={matchCtx.homeScore}
                awayScore={matchCtx.awayScore}
                attackingDirection={frame.attackingMex ? "right" : "left"}
                minute={matchCtx.minute}
                formationHome={richCtx.formationHome}
                formationAway={richCtx.formationAway}
                topPerformerName={matchCtx.topPerformer.player.name}
                topPerformerTeam={matchCtx.topPerformer.teamName}
              />
              )}
              <Suspense
                fallback={
                  <div className="flex h-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-background to-accent/20">
                    <div className="relative h-10 w-10">
                      <div className="absolute inset-0 rounded-full border-2 border-primary/20" />
                      <div className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-primary" />
                    </div>
                    <div className="text-[11px] font-bold tracking-wider text-foreground/80">
                      {t("loading_live_tracker")}
                    </div>
                  </div>
                }
              >
                <ProfessionalRadar3D
                  elapsed={elapsed}
                  isGoal={showGoal}
                  lang={lang}
                  onXRay={(info) =>
                    setXray({ ...info, visible: true })
                  }
                  hideLastTouch={hasOfficialTelemetry}
                />
              </Suspense>

              {/* Overlay HUD bottom-left (kept clear of the team identity bar) */}
              <div className="pointer-events-none absolute left-3 bottom-3 flex items-center gap-2 rounded-lg bg-background/50 px-2.5 py-1 text-[10px] font-bold backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse-dot" />
                <span>{t("live_tracker")}</span>
              </div>
              {/* in-danger flag */}
              {frame.inDanger && !showGoal && (
                <div className="pointer-events-none absolute bottom-3 right-3 rounded-lg bg-primary/90 px-2.5 py-1 text-[10px] font-bold text-primary-foreground">
                  {t("danger_zone")}
                </div>
              )}

              <PredictiveVector
                visible={xray.visible && frame.inDanger}
                goalPct={xray.goalPct}
                keyPassPct={xray.keyPassPct}
                defenderRiskHigh={xray.defenderRiskHigh}
              />

              <EdgeAlerts frame={frame} xgPct={xray.goalPct} />

              <AttentionYieldGroup selfKey="story_arc">
                <PublicEmotionalSignalOverlay
                  className="absolute left-2 top-12 sm:left-3 sm:top-3 z-30 w-[min(260px,calc(100%-1rem))] sm:w-[min(320px,calc(100%-1.5rem))]"
                  homeLabel={matchCtx.homeTeamName}
                  awayLabel={matchCtx.awayTeamName}
                  scoreHome={headerHomeScore}
                  scoreAway={headerAwayScore}
                  lang={lang === "ES" || lang === "EN" || lang === "PT" ? lang : "ES"}
                />
              </AttentionYieldGroup>

              {/* Live hype meter — tap-to-react with collective heat bar */}
              <HypeMeterButton
                matchId={hub.activeId}
                homeCode={matchCtx.homeCode}
                awayCode={matchCtx.awayCode}
                homeTeamName={matchCtx.homeTeamName}
                awayTeamName={matchCtx.awayTeamName}
              />

            </div>

            <Suspense fallback={null}>
              <SponsoredReactionCard matchId={matchCtx.id ?? null} />
            </Suspense>

            {/* Second-screen interactive widgets */}
            <div className="mt-3">
              <SpoilerSyncSlider />
            </div>
            <div className="mt-3 grid grid-cols-1 gap-2 lg:grid-cols-2">
              <VarWidget frame={frame} elapsed={elapsed} />
              <EventTimeline
                fixtureId={fixtureIdForStats}
                lifecycle={
                  matchCtx.status === "FT"
                    ? "FINISHED"
                    : matchCtx.status === "SCHEDULED"
                      ? "SCHEDULED"
                      : "LIVE"
                }
                minute={frame.minute}
              />
            </div>

            {/* Real Data Phase 3 — Official Lineups */}
            <div className="mt-3">
              <OfficialLineups
                fixtureId={fixtureIdForStats}
                lifecycle={
                  matchCtx.status === "FT"
                    ? "FINISHED"
                    : matchCtx.status === "SCHEDULED"
                      ? "SCHEDULED"
                      : "LIVE"
                }
                homeCode={matchCtx.homeCode}
                awayCode={matchCtx.awayCode}
              />
            </div>

            {/* Real Data Phase 4 — Official Player Statistics */}
            <div className="mt-3">
              <OfficialPlayerStats
                fixtureId={fixtureIdForStats}
                lifecycle={
                  matchCtx.status === "FT"
                    ? "FINISHED"
                    : matchCtx.status === "SCHEDULED"
                      ? "SCHEDULED"
                      : "LIVE"
                }
                homeCode={matchCtx.homeCode}
                awayCode={matchCtx.awayCode}
              />
            </div>

            <CompactTacticalXrayCard
              visible={xray.visible}
              goalPct={xray.goalPct}
              keyPassPct={xray.keyPassPct}
              defenderRiskHigh={xray.defenderRiskHigh}
            />

            {/* Tactical mini-map (22 players) */}
            <div className="mt-3">
              <TacticalMiniMap elapsed={elapsed} homeCode={matchCtx.homeCode} awayCode={matchCtx.awayCode} />
            </div>

            {/* Stats below radar on desktop */}
            <div className={`mt-4 ${tab === "radar" ? "" : "hidden lg:block"}`}>
              <QuickStats
                lifecycle={matchCtx.status === "FT" ? "FINISHED" : matchCtx.status === "SCHEDULED" ? "SCHEDULED" : "LIVE"}
                stats={matchInsights.stats}
                isLoading={matchInsights.isLoading}
                isError={matchInsights.isError}
                homeCode={matchCtx.homeCode}
                awayCode={matchCtx.awayCode}
              />
            </div>
            </>
          )}
          </section>

          {/* Side panel */}
          {tab === "chat" && (
            <style>{`
              @media (max-width: 1023px) {
                [aria-label="Quick share"],
                [aria-label="Abrir encuesta rápida"] { display: none !important; }
              }
            `}</style>
          )}
          <aside
            className={`${PREVIEW_MODE || tab === "chat" ? "block" : "hidden"} lg:block lg:col-span-3`}
            aria-label="Chat panel"
          >
            <div className="h-[calc(100vh-220px)] min-h-[620px] bg-background pt-2 lg:h-[calc(100vh-140px)] lg:min-h-0 lg:p-0 lg:bg-transparent">
              <CommunicationTabs
                key={hub.activeId}
                messages={messages}
                onSend={handleSend}
                onAiReply={handleAiReply}
                matchId={hub.activeId}
                matchContext={`${richAiContext}\nPhase: ${matchClock.phaseLabel} (${matchClock.display})\nTemporal: ${aiTemporalContext}\nRoster: ${rosterSummary(hub.activeId)}`}
              />
            </div>
          </aside>

          {/* mobile-only stats tab */}
          <section
            className={`${PREVIEW_MODE || tab === "stats" ? "block" : "hidden"} lg:hidden`}
            aria-label="Stats panel"
          >
            <QuickStats
              lifecycle={matchCtx.status === "FT" ? "FINISHED" : matchCtx.status === "SCHEDULED" ? "SCHEDULED" : "LIVE"}
              stats={matchInsights.stats}
              isLoading={matchInsights.isLoading}
              isError={matchInsights.isError}
              homeCode={matchCtx.homeCode}
              awayCode={matchCtx.awayCode}
            />
          </section>

          {/* mobile-only profile tab */}
          <section
            className={`${tab === "profile" ? "block" : "hidden"} lg:hidden`}
            aria-label="Profile panel"
          >
            {tab === "profile" && <div className="space-y-3">
              {(() => {
                const meta = (user?.user_metadata ?? {}) as { full_name?: string; name?: string; avatar_url?: string; picture?: string };
                const displayName = meta.full_name || meta.name || user?.email?.split("@")[0] || t("guest_label");
                const avatar = meta.avatar_url || meta.picture;
                const initial = (displayName || "?").trim().charAt(0).toUpperCase();
                return (
                  <div className="rounded-2xl glass p-6 text-center">
                    {avatar ? (
                      <img
                        src={avatar}
                        alt={displayName}
                        className="mx-auto h-16 w-16 rounded-full object-cover ring-2 ring-primary/40"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-gradient-to-br from-[oklch(0.58_0.24_27)] to-[oklch(0.7_0.22_15)] text-xl font-black text-white glow-red">
                        {initial}
                      </div>
                    )}
                    <div className="mt-3 text-sm font-bold">{displayName}</div>
                    {user?.email ? (
                      <div className="text-xs text-muted-foreground">{user.email}</div>
                    ) : (
                      <div className="text-xs text-muted-foreground">{t("guest_label")}</div>
                    )}
                  </div>
                );
              })()}
              <ProfileQuickActions onNavigateChat={() => setTab("chat")} />
              <Suspense fallback={null}><FanDnaCard shareable /></Suspense>
              {isBetting && <AuditTransparencyTab />}
              {isAuthenticated && (
                <button
                  type="button"
                  onClick={async () => {
                    try { await logout(); } catch { /* ignore */ }
                    try {
                      window.localStorage.removeItem("ss2026_registered");
                      window.localStorage.removeItem("ss2026_session_id");
                    } catch { /* ignore */ }
                    window.location.replace("/welcome");
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm font-bold text-destructive transition active:scale-[0.99]"
                >
                  <LogOut className="h-4 w-4" />
                  {t("btn_logout") || "Cerrar sesión"}
                </button>
              )}
            </div>}
          </section>
        </div>
        {/* PREVIEW cleanup — encuestas y calendario van DEBAJO de la cancha
            para dejarla como protagonista absoluto. */}
        {PREVIEW_MODE && (
          <div className="mt-4 space-y-3">
            <SponsorRivalryPoll matchId={matchCtx.id ?? null} />
          </div>
        )}
        {isMobile && tab !== "chat" && (
          <div className="mt-4 space-y-3 lg:hidden" aria-label="Mobile inline actions">
            {!showPredictionHub && !halftimeOpen && !advStatsOpen && !cleanMode && !keyboardOpen && (
              <>
                <QuinielaOpenCTA inline />
                <DiscoveryDailyCTA inline />
                <GuerrillaInviteButton compact />
                <MobileShareInlineCard />
              </>
            )}
            {!showPredictionHub && !halftimeOpen && !advStatsOpen && (
              <LivePollWidget surface="live" inline />
            )}
            <MatchPulse
              matchId={hub.activeMatch.id}
              minute={frame.minute}
              firstGoalMinute={firstGoalMinute}
              firstGoalAt={firstGoalAt}
              country={geo?.country ?? null}
              nation={geo?.country ?? null}
              activeForMs={activeForMs}
              suppressed={showGoal || frame.inDanger || frame.freeKick}
              matchStatus={matchCtx.status}
              inline
            />
          </div>
        )}
        {/* HOME-SIMPLIFICATION P1 — MOVED below the fold. */}
        {matchCtx.status !== "LIVE" && (
          <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <ClipOfTheDayCard />
            <AudioSyncEduCard />
          </div>
        )}
      </main>

      {/* Floating CTA */}
      {!isMobile && <AttentionYieldGroup selfKey="participa_fab">
        <button
        ref={predictionsFab.ref}
        onPointerDown={predictionsFab.onPointerDown}
        onClick={(e) => {
          if (predictionsFab.isDragging) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          handleParticipate();
        }}
        aria-label={t(ctaLabelKey(ctaContext))}
        aria-hidden={cleanMode || keyboardOpen}
        className={
          "fixed right-4 z-30 hidden shrink-0 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[oklch(0.58_0.24_27)] to-[oklch(0.7_0.22_15)] px-5 text-sm font-bold text-primary-foreground shadow-[0_10px_40px_-5px_var(--univision-red-glow)] transition active:scale-95 lg:right-6 lg:flex " +
          (predictionsFab.style.position === "fixed"
            ? ""
            : "bottom-[calc(88px+env(safe-area-inset-bottom))] lg:bottom-6 animate-float-y ") +
          (cleanMode || keyboardOpen ? "pointer-events-none opacity-0" : "opacity-100 animate-float-y")
        }
        style={{
          height: 48,
          maxWidth: 220,
          minWidth: 140,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          userSelect: "none",
          ...predictionsFab.style,
        }}
      >
        <Sparkles className="h-4 w-4 shrink-0" />
        <span className="truncate">{t(ctaLabelKey(ctaContext))}</span>
        </button>
      </AttentionYieldGroup>}

      <BottomNav active={tab} onChange={setTab} />
      <Suspense fallback={null}>
        <AutoViralMomentCapsule />
        <PrestigeCelebrationToast />
        <GlobalTakeoverCapsule
          ctx={{
            homeLabel: matchCtx.homeTeamName ?? matchCtx.homeCode ?? "Local",
            awayLabel: matchCtx.awayTeamName ?? matchCtx.awayCode ?? "Visitante",
            scoreHome: matchCtx.homeScore ?? 0,
            scoreAway: matchCtx.awayScore ?? 0,
          }}
          minute={headerMinute ?? 0}
          isLive={matchCtx.status === "LIVE"}
        />
      </Suspense>
      <AttentionYieldGroup selfKey="retention_fab">
        {/* HOME_MOBILE_CLEANUP — only show RetentionFab when LIVE to avoid
            FAB pile-up against participa_fab / QuinielaOpenCTA on mobile. */}
        {!isMobile && matchCtx.status === "LIVE" && (
          <div>
            <RetentionFab
              matchMinute={headerMinute}
              isLive={matchCtx.status === "LIVE"}
              emotionalCtx={{
                homeLabel: matchCtx.homeTeamName ?? matchCtx.homeCode ?? "Local",
                awayLabel: matchCtx.awayTeamName ?? matchCtx.awayCode ?? "Visitante",
                scoreHome: matchCtx.homeScore ?? 0,
                scoreAway: matchCtx.awayScore ?? 0,
              }}
            />
          </div>
        )}
      </AttentionYieldGroup>
      {legacyMocksEnabled && showPredictionHub && (
        <Suspense fallback={null}>
          <PredictionHubSheet
            open={showPredictionHub}
            onClose={() => setShowPredictionHub(false)}
            matchId={matchCtx.id}
          />
        </Suspense>
      )}

      {/* Ambient quiniela CTA — no new polling, reuses bracket query cache */}
      {!showPredictionHub && !halftimeOpen && !advStatsOpen && !cleanMode && !keyboardOpen && !isMobile && (
        <AttentionYieldGroup selfKey="quiniela_cta">
          <div className="hidden lg:block">
            <QuinielaOpenCTA />
          </div>
        </AttentionYieldGroup>
      )}

      {/* PR-DISCOVERY-01 · P1-3 — Daily check-in / streak discoverability.
          HOME_MOBILE_CLEANUP — hide on small screens to reduce FAB / CTA
          pile-up; remains visible on tablet+ where space allows. */}
      {!showPredictionHub && !halftimeOpen && !advStatsOpen && !cleanMode && !keyboardOpen && !isMobile && (
        <div className="hidden sm:block">
          <DiscoveryDailyCTA />
        </div>
      )}

      {/* WORLD-CUP-VIRAL-ACTIVATION-P0 — viral invite CTA, hidden while overlays own the screen */}
      {!showPredictionHub && !halftimeOpen && !advStatsOpen && !cleanMode && !keyboardOpen && (
        <div className="hidden px-3 pb-2 lg:block">
          <GuerrillaInviteButton compact />
        </div>
      )}

      {/* Live polls — auto-managed queue, hidden while a major surface is open */}
      {tab !== "chat" && !showPredictionHub && !halftimeOpen && !advStatsOpen && !isMobile && (
        <LivePollWidget surface="live" />
      )}

      {/* Contextual prediction card — gated behind legacyPredictionMocks
          flag (PR-40C). The real prediction surface lives at /quinielas
          and uses MundialPredictionFloatingCard. */}
      {legacyMocksEnabled && (
        <PredictionCard
          visible={!!predictionCard && !showPredictionHub && !halftimeOpen && !advStatsOpen && !cleanMode && !keyboardOpen}
          question={predictionCard?.q}
          questionId={predictionCard?.id}
          onClose={() => setPredictionCard(null)}
        />
      )}

      {showGoal && (
        <Suspense fallback={null}>
          <GoalCelebration
            show={showGoal}
            homeCode={matchCtx.homeCode}
            awayCode={matchCtx.awayCode}
            homeFlag={matchCtx.homeFlag}
            awayFlag={matchCtx.awayFlag}
            homeScore={matchCtx.homeScore}
            awayScore={matchCtx.awayScore}
            minute={matchCtx.minute}
          />
          <GoalMomentBoost
            show={showGoal}
            homeCode={matchCtx.homeCode}
            awayCode={matchCtx.awayCode}
            homeFlag={matchCtx.homeFlag}
            awayFlag={matchCtx.awayFlag}
            homeName={matchCtx.homeTeamName}
            awayName={matchCtx.awayTeamName}
            homeScore={matchCtx.homeScore}
            awayScore={matchCtx.awayScore}
            minute={matchCtx.minute}
          />
        </Suspense>
      )}
      <Suspense fallback={null}>
        <CinematicMatchRecap
          matchId={matchCtx.id}
          status={matchCtx.status}
          homeCode={matchCtx.homeCode}
          awayCode={matchCtx.awayCode}
          homeName={matchCtx.homeTeamName}
          awayName={matchCtx.awayTeamName}
          homeFlag={matchCtx.homeFlag}
          awayFlag={matchCtx.awayFlag}
          homeScore={matchCtx.homeScore}
          awayScore={matchCtx.awayScore}
          minute={matchCtx.minute}
        />
      </Suspense>
      <NationOvertakeToast />
      <InstallBanner />
      <Suspense fallback={null}><DemoControlPanel /></Suspense>

      {/* First-time onboarding overlay — lazy loaded, radar stays visible behind */}
      {onboarding.isOpen && (
        <Suspense fallback={null}>
          <TacticalQuickStartGuide
            open={onboarding.isOpen}
            onClose={onboarding.close}
          />
        </Suspense>
      )}

      {rewardsOpen && (
        <Suspense fallback={null}>
          <RewardsCatalog
            open={rewardsOpen}
            onClose={() => setRewardsOpen(false)}
            points={fanPoints.points}
            onRedeemed={() => void fanPoints.refresh()}
          />
        </Suspense>
      )}

      {isBetting && bonus && (
        <SmartBonusPopup offer={bonus} onClose={() => setBonus(null)} />
      )}

      {halftimeOpen && (
        <Suspense fallback={null}>
          <HalftimeEngagementHub
            key={hub.activeId}
            open={halftimeOpen}
            trigger={hubTrigger}
            onClose={() => setHalftimeOpen(false)}
            onMinimize={() => setHalftimeOpen(false)}
            fanPoints={fanPoints.points}
            matchContext={{
              home: hub.activeMatch.homeCode,
              away: hub.activeMatch.awayCode,
              minute: headerMinute,
              homeTeamName: matchCtx.homeTeamName,
              awayTeamName: matchCtx.awayTeamName,
              topPerformerName: matchCtx.topPerformer.player.name,
              topPerformerTeam: matchCtx.topPerformer.teamName,
              topPerformerMetric: `${matchCtx.topPerformer.player.position ?? ""}`.trim() || undefined,
              formationHome: richCtx.formationHome,
              formationAway: richCtx.formationAway,
            }}
          />
        </Suspense>
      )}

      {advStatsOpen && (
        <Suspense fallback={null}>
          <AdvancedStatsModal
            open={advStatsOpen}
            onClose={() => setAdvStatsOpen(false)}
            matchContext={{
              home: hub.activeMatch.homeCode,
              away: hub.activeMatch.awayCode,
              minute: headerMinute,
              homeTeamName: matchCtx.homeTeamName,
              awayTeamName: matchCtx.awayTeamName,
              topPerformerName: matchCtx.topPerformer.player.name,
              topPerformerTeam: matchCtx.topPerformer.teamName,
            }}
          />
        </Suspense>
      )}
      {!isMobile && <ShareButton />}
      <Suspense fallback={null}>
        {/* HOME_MOBILE_CLEANUP — canonical surface lives in /quinielas. On
            small screens this floating card collides with participa_fab
            and BottomNav; only render on lg+ as a desktop convenience. */}
        {!isMobile && <MundialPredictionFloatingCard matchId={matchCtx.id ?? undefined} />}
        <SocialPulseBanner
          matchId={hub.activeMatch.id ?? null}
          goalActive={showGoal || frame.inDanger || frame.freeKick}
        />
      </Suspense>
      {tab !== "chat" && !isMobile && <MatchPulse
        matchId={hub.activeMatch.id}
        minute={frame.minute}
        firstGoalMinute={firstGoalMinute}
        firstGoalAt={firstGoalAt}
        country={geo?.country ?? null}
        nation={geo?.country ?? null}
        activeForMs={activeForMs}
        suppressed={showGoal || frame.inDanger || frame.freeKick}
        matchStatus={matchCtx.status}
      />}
      {/* PR-UX-VIRAL-01 — force nation selection on first LIVE match. */}
      {matchCtx.status === "LIVE" && <NationGate surface="home_live" />}
    </div>
  );
}
